import React from 'react';
import {Navigate, Outlet, Route, Routes, useLocation} from 'react-router-dom';
import {isJwtExpired} from './lib/auth';
import {useCurrentUser} from './hooks/useCurrentUser';
import {useCanAnyZone} from './hooks/useCan';
import LoginPage from './components/Auth/LoginPage';
import {OccupancyPlanEditor} from './components/OccupancyPlanEditor';
import LeaveReviewPage from './pages/Public/LeaveReviewPage';
import WifiPortalPage from './pages/Public/WifiPortalPage';
import {Layout} from './components/layout/Layout';
import BranchManagement from './pages/BranchManagement/BranchManagement';
import WorkerDashboardPage from './pages/Usage/WorkerDashboardPage';
import OpsTasksPage from './pages/Usage/OpsTasksPage';
import VisitsPage from './pages/Usage/VisitsPage';
import VisitDetailPage from './pages/Usage/VisitDetailPage';
import PortfolioPage from './pages/Usage/PortfolioPage';
import ReportValidationPage from './pages/Service/ReportValidationPage';
import PredictionsFeedPage from './pages/Service/PredictionsFeedPage';
import ChartDemoPage from './pages/dev/ChartDemoPage';
import OverviewReportPage from './pages/Usage/OverviewReportPage';
import AnalyticsPage from './pages/Management/AnalyticsPage';
import StatusPage from './pages/Management/StatusPage';
import StatusAllPage from './pages/Management/StatusAllPage';
import CalibrationPage from './pages/Management/CalibrationPage';
import AccessPage from './pages/BranchManagement/AccessPage';
import RedeemPage from './pages/Invite/RedeemPage';
import TgOidcCallbackPage from './pages/Auth/TgOidcCallbackPage';
import AccountSettingsPage from './pages/Settings/AccountSettingsPage';
import UsersListPage from './pages/Admin/UsersListPage';
import BranchesListPage from './pages/Admin/BranchesListPage';
import GroupsAdminPage from './pages/Admin/GroupsAdminPage';
import UserAdminPage from './pages/Admin/UserAdminPage';
import LocalComputersListPage from './pages/Admin/LocalComputersListPage';
import LocalComputerDetailPage from './pages/Admin/LocalComputerDetailPage';
import HostInfraPage from './pages/Admin/HostInfraPage';
import DataPage from './pages/Admin/DataPage';
import SetupWizardPage from './pages/SetupWizard/SetupWizardPage';

/**
 * SPA redirect that preserves the inbound query string (so ?zone survives) and
 * can inject extra params (e.g. the legacy /usage/summaries → /usage/report?z=day
 * collapse). Replaces the route so the back button doesn't bounce.
 */
const Redirect = ({ to, addParams }: { to: string; addParams?: Record<string, string> }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (addParams) {
    for (const [k, v] of Object.entries(addParams)) params.set(k, v);
  }
  const qs = params.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
};

const PrivateRoutes = () => {
  const jwt = localStorage.getItem('jwt');
  const refresh = localStorage.getItem('refresh');
  const location = useLocation();
  // If JWT is missing, or expired with no refresh token to recover from, bounce
  // to /login. When the access token is expired but a refresh token is present,
  // let the routes mount — the axios 401 interceptor will refresh on the next
  // API call.
  if (!jwt || (isJwtExpired(jwt) && !refresh)) {
    if (jwt) {
      localStorage.removeItem('jwt');
      localStorage.removeItem('username');
      localStorage.removeItem('me:last');
      localStorage.removeItem('zones:last');
    }
    // Carry the ORIGINAL params through verbatim (useTelegramAutoLogin's
    // bot-deep-link exchange reads `tg_token` straight off window.location.search
    // on /login, not through `next`) AND add `next=<full path+search>` — every
    // OTHER login path (password/handleLogin, TG widget/handleTelegramAuth, TG
    // OIDC/TelegramLoginButton+TgOidcCallbackPage) already reads `next` to
    // restore the pre-login destination, but nothing was ever setting it here:
    // an unauthenticated visit to e.g. /usage/report?...&target=queue landed on
    // /login with those params dangling unread, so every login path fell back to
    // nextTarget='/' → dashboard, silently dropping target=queue/from/to (root
    // cause of the queue-deeplink "doesn't open the fullscreen view" report,
    // 2026-09-04 — reproduced on staging: an unauth deep-link visit redirected to
    // /login WITHOUT a `next` param before this fix).
    const params = new URLSearchParams(location.search);
    params.set('next', `${location.pathname}${location.search}`);
    return <Navigate to={`/login?${params.toString()}`} replace />;
  }
  return <Outlet />;
};

/** Blocks /management/* for users who cannot edit config in any zone. */
const ManagementGuard = () => {
  const { isLoading } = useCurrentUser();
  const canManage = useCanAnyZone('zone.config.edit');
  // Wait for user to load before deciding — avoids flash redirect for authorized users.
  if (isLoading) return null;
  if (!canManage) return <Navigate to="/usage/dashboard" replace />;
  return <Outlet />;
};

/** Blocks /admin/* and the Экспериментальные pages for non-superusers. */
const SuperuserGuard = () => {
  const { user, isLoading } = useCurrentUser();
  if (isLoading) return null;
  if (!user?.is_superuser) return <Navigate to="/usage/dashboard" replace />;
  return <Outlet />;
};

/** Blocks the Служебные pages (report validation, data) for non-staff.
 *  Staff is a superset of superusers. */
const StaffGuard = () => {
  const { user, isLoading } = useCurrentUser();
  if (isLoading) return null;
  if (!user?.is_staff && !user?.is_superuser) return <Navigate to="/usage/dashboard" replace />;
  return <Outlet />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth" element={<LoginPage />} />
      <Route path="/auth/tg/callback" element={<TgOidcCallbackPage />} />
      <Route path="/invite/:token" element={<RedeemPage />} />
      <Route path="/ple" element={<OccupancyPlanEditor />} />
      {/* Public review form behind a uuid (no login, no enumerable ids) — the QR
          code a branch prints points here. Outside PrivateRoutes on purpose. */}
      <Route path="/leave_review/:publicId" element={<LeaveReviewPage />} />
      {/* Гостевой портал Wi-Fi — тоже публичный, без входа. */}
      <Route path="/wifi/:publicId" element={<WifiPortalPage />} />

      <Route element={<PrivateRoutes />}>
        <Route element={<Layout />}>
          <Route index element={<Navigate to="/usage/dashboard" replace />} />
          {/* Live + Аналитика — prod shape (one-branch; pick the branch in the header). */}
          <Route path="/usage/dashboard" element={<WorkerDashboardPage />} />
          <Route path="/usage/tasks" element={<OpsTasksPage />} />
          {/* VISITS-3 (#176): quality-check visits, same Live-tab shape as
              Задачи на смену — list page + a per-visit detail page (draft
              edit / submitted view, same component). */}
          <Route path="/usage/visits" element={<VisitsPage />} />
          <Route path="/usage/visits/:visitId" element={<VisitDetailPage />} />
          {/* Merged "Отчёт" — folds the former Итоги дня + Итоги недели into one
              section behind a День/Неделя picker. Read-only for everyone; report
              validation lives on the Служебные "Проверка отчётов" page. */}
          <Route path="/usage/report" element={<OverviewReportPage />} />
          {/* Legacy entry points → the merged report (preserve cached links).
              ?z picks the zoom so an old Итоги недели link lands on the week. */}
          <Route path="/usage/summaries" element={<Redirect to="/usage/report" addParams={{ z: 'day' }} />} />
          <Route path="/usage/weekly-report" element={<Redirect to="/usage/report" addParams={{ z: 'week' }} />} />
          {/* Тепловая карта is now a tab of Статистика; preserve cached links. */}
          <Route path="/usage/heatmap" element={<Redirect to="/management/analytics" addParams={{ tab: 'heatmap' }} />} />
          {/* /usage/now was a brief experiment — keep a redirect so any cached link lands. */}
          <Route path="/usage/now" element={<Redirect to="/usage/dashboard" />} />
          {/* Read-only stats page — OUTSIDE ManagementGuard on purpose: the
              sidebar shows Статистика to reports.menu users, and it absorbed
              the heatmap, which was never manager-gated. */}
          <Route path="/management/analytics" element={<AnalyticsPage />} />
          {/* «Статус» прятали на проде с 08.10; открыт Алексеем 21.10 (#7014). */}
          <Route path="/management/status" element={<StatusPage />} />
          <Route path="/management/status/all" element={<StatusAllPage />} />
          <Route element={<ManagementGuard />}>
            <Route path="/management" element={<Navigate to="/management/zoning" replace />} />
            <Route path="/management/general" element={<BranchManagement />} />
            <Route path="/management/zoning" element={<BranchManagement />} />
            <Route path="/management/plan" element={<BranchManagement />} />
            <Route path="/management/checklists" element={<BranchManagement />} />
            <Route path="/management/visits" element={<BranchManagement />} />
            <Route path="/management/sales-plan" element={<BranchManagement />} />
            <Route path="/management/receipts" element={<BranchManagement />} />
            <Route path="/management/queues-staff" element={<BranchManagement />} />
            <Route path="/management/reviews" element={<BranchManagement />} />
            <Route path="/management/speech" element={<BranchManagement />} />
            <Route path="/management/telegram" element={<BranchManagement />} />
            <Route path="/management/equipment" element={<BranchManagement />} />
            <Route path="/management/integrations" element={<BranchManagement />} />
            <Route path="/management/setup-wizard" element={<SetupWizardPage />} />
            <Route path="/management/access" element={<AccessPage />} />
          </Route>
          <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
          <Route path="/settings/account" element={<AccountSettingsPage />} />
          {/* Служебные — staff (is_staff), a superset of superusers. Report
              validation + the data annotation tool. */}
          <Route element={<StaffGuard />}>
            <Route path="/service/report-validation" element={<ReportValidationPage />} />
            <Route path="/service/predictions" element={<PredictionsFeedPage />} />
            {/* #159 «Калибровка полигонов» — moved from Настройка to Служебные
                (Alexey 2026-08-25): same audience as Проверка отчётов — our own
                staff, not the client — so it's staff-only (is_staff) and sits
                right next to it. Zone comes from the header branch picker
                (useCurrentZone), not ManagementGuard, so it works unchanged.
                Path kept as /management/calibration to preserve cached links. */}
            <Route path="/management/calibration" element={<CalibrationPage />} />
            {/* Throwaway standalone harness for the annotatable chart (item E).
                Not in the sidebar; remove when the chart mounts in the editor. */}
            <Route path="/dev/chart-demo" element={<ChartDemoPage />} />
            <Route path="/admin/data" element={<DataPage />} />
          </Route>
          {/* Экспериментальные + admin — superuser only. */}
          <Route element={<SuperuserGuard />}>
            <Route path="/usage/portfolio" element={<PortfolioPage />} />
            <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/branches" element={<BranchesListPage />} />
            <Route path="/admin/users" element={<UsersListPage />} />
            <Route path="/admin/users/:userId" element={<UserAdminPage />} />
            <Route path="/admin/groups" element={<GroupsAdminPage />} />
            <Route path="/admin/local-computers" element={<LocalComputersListPage />} />
            <Route path="/admin/local-computers/:id" element={<LocalComputerDetailPage />} />
            <Route path="/admin/host-infra" element={<HostInfraPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/usage/dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRoutes;
